import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  AccessTime,
  CalendarToday,
  MailOutline,
  FiberManualRecord,
  WhatsApp,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { getGuest, Guest } from '../services/guestsService';
import { getUserEvents, updateEvent, Event } from '../services/eventsService';
import { TopBar } from '../layout/TopBar';
import { getComponentTokens } from '../styles/theme';

export const GuestDetailsPage: React.FC = () => {
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const { guestId } = useParams<{ guestId: string }>();
  const navigate = useNavigate();
  const componentTokens = getComponentTokens(isDark);
  const [guest, setGuest] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(true);
  const [addToEventDialog, setAddToEventDialog] = useState(false);
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [addingToEvents, setAddingToEvents] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Invite states
  const [showInviteEventDialog, setShowInviteEventDialog] = useState(false);
  const [showInviteMethodDialog, setShowInviteMethodDialog] = useState(false);
  const [selectedEventForInvite, setSelectedEventForInvite] = useState<Event | null>(null);
  const [eventsForInvite, setEventsForInvite] = useState<Event[]>([]);

  const getMaritalStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      single: 'Solteiro(a)',
      married: 'Casado(a)',
      divorced: 'Divorciado(a)',
      widowed: 'Viúvo(a)',
      other: 'Outro'
    };
    return labels[status] || status;
  };

  useEffect(() => {
    const loadGuest = async () => {
      if (!guestId) {
        navigate('/guests');
        return;
      }

      try {
        setLoading(true);
        const guestData = await getGuest(guestId);

        if (!guestData || guestData.userId !== user?.uid) {
          navigate('/guests');
          return;
        }

        setGuest(guestData);
      } catch (error) {
        console.error('Erro ao carregar convidado:', error);
        navigate('/guests');
      } finally {
        setLoading(false);
      }
    };

    loadGuest();
  }, [guestId, user?.uid, navigate]);

  useEffect(() => {
    const loadUserEvents = async () => {
      if (!user?.uid || !addToEventDialog) return;

      try {
        setLoadingEvents(true);
        const events = await getUserEvents(user.uid);

        const futureEvents = events.filter(event => {
          const eventDate = new Date(event.date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return eventDate >= today;
        });

        setUserEvents(futureEvents);

        if (guest) {
          const eventsWithGuest = futureEvents
            .filter(event => event.guests?.includes(guest.id!))
            .map(event => event.id);
          setSelectedEvents(eventsWithGuest);
        }
      } catch (error) {
        console.error('Erro ao carregar eventos:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao carregar eventos',
          severity: 'error',
        });
      } finally {
        setLoadingEvents(false);
      }
    };

    loadUserEvents();
  }, [user?.uid, addToEventDialog, guest]);

  const handleOpenAddToEvent = () => {
    if (!guest) return;
    setAddToEventDialog(true);
  };

  const handleToggleEvent = (eventId: string) => {
    setSelectedEvents(prev =>
      prev.includes(eventId)
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  const handleAddToEvents = async () => {
    if (!guest || !user?.uid) return;

    try {
      setAddingToEvents(true);

      const updatePromises = userEvents.map(async (event) => {
        const currentGuests = event.guests || [];
        const hasGuest = currentGuests.includes(guest.id!);
        const shouldHaveGuest = selectedEvents.includes(event.id);

        if (hasGuest && !shouldHaveGuest) {
          const updatedGuests = currentGuests.filter(id => id !== guest.id!);
          await updateEvent(event.id, { guests: updatedGuests });
        } else if (!hasGuest && shouldHaveGuest) {
          const updatedGuests = [...currentGuests, guest.id!];
          await updateEvent(event.id, { guests: updatedGuests });
        }
      });

      await Promise.all(updatePromises);

      const selectedCount = selectedEvents.length;
      setSnackbar({
        open: true,
        message: selectedCount > 0
          ? `Convidado adicionado a ${selectedCount} evento(s) com sucesso!`
          : 'Convidado removido dos eventos',
        severity: 'success',
      });
      setAddToEventDialog(false);
    } catch (error) {
      console.error('Erro ao vincular convidado aos eventos:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao vincular convidado aos eventos',
        severity: 'error',
      });
    } finally {
      setAddingToEvents(false);
    }
  };

  const handleSendInviteClick = async () => {
    if (!guest || !user?.uid) return;

    try {
      setLoadingEvents(true);
      const events = await getUserEvents(user.uid);

      const futureEvents = events.filter(event => {
        const eventDate = new Date(event.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return eventDate >= today;
      });

      // Filter events where the guest is already added
      const guestEvents = futureEvents.filter(event => event.guests?.includes(guest.id!));

      if (guestEvents.length === 0) {
        setSnackbar({
          open: true,
          message: 'Este convidado não está vinculado a nenhum evento futuro. Adicione-o a um evento primeiro.',
          severity: 'info'
        });
        return;
      }

      setEventsForInvite(guestEvents);
      setShowInviteEventDialog(true);
    } catch (error) {
      console.error('Erro ao carregar eventos para convite:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar eventos',
        severity: 'error'
      });
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleSelectEventForInvite = (event: Event) => {
    setSelectedEventForInvite(event);
    setShowInviteEventDialog(false);
    setShowInviteMethodDialog(true);
  };

  const handleSendWhatsApp = () => {
    if (!selectedEventForInvite || !guest?.phone) return;

    const event = selectedEventForInvite;
    const eventDate = new Date(event.date);
    const formattedDate = eventDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

    const message = `Olá! Você foi convidado para *${event.title}*!\n\n` +
      `Data: ${formattedDate}\n` +
      `Hora: ${event.time}\n` +
      `Local: ${event.location || 'A definir'}\n\n` +
      `${event.description ? `${event.description}\n\n` : ''}` +
      `${event.outfit ? `O que usar: ${event.outfit}\n\n` : ''}` +
      `Aguardo você!\n\n` +
      `Enviado pelo Hostly App`;

    const cleanPhone = guest.phone.replace(/\D/g, '');
    const phone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    setShowInviteMethodDialog(false);
    setSelectedEventForInvite(null);
  };

  const handleSendEmail = () => {
    if (!selectedEventForInvite || !guest?.email) return;

    const event = selectedEventForInvite;
    const eventDate = new Date(event.date);
    const formattedDate = eventDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

    const subject = `Convite: ${event.title}`;
    const body = `Olá!\n\nVocê foi convidado para ${event.title}!\n\n` +
      `Data: ${formattedDate}\n` +
      `Hora: ${event.time}\n` +
      `Local: ${event.location || 'A definir'}\n\n` +
      `${event.description ? `Detalhes: ${event.description}\n\n` : ''}` +
      `${event.outfit ? `O que usar: ${event.outfit}\n\n` : ''}` +
      `Aguardo você!`;

    window.location.href = `mailto:${guest.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    setShowInviteMethodDialog(false);
    setSelectedEventForInvite(null);
  };

  if (loading) {
    return (
      <>
        <TopBar title="Detalhes do Convidado" showBackButton={true} onBackClick={() => navigate('/guests')} />
        <Box sx={componentTokens.guestDetails.skeleton}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        </Box>
      </>
    );
  }

  if (!guest) {
    return null;
  }

  return (
    <>
      <TopBar title="Detalhes" showBackButton={true} onBackClick={() => navigate('/guests')} />
      <Box sx={{ backgroundColor: theme.colors.background, minHeight: '100vh', pt: '70px' }}>
        <Container maxWidth="sm" sx={{ py: { xs: 2, md: 4 } }}>
          <Paper sx={{ p: { xs: 2, md: 4 }, backgroundColor: theme.colors.card, borderRadius: 2, border: 'none', boxShadow: 'none' }}>
            {/* Header */}
            <Grid container spacing={2} alignItems="center">
              <Grid size="auto">
                <Avatar sx={{ width: 80, height: 80, backgroundColor: theme.colors.secondary, fontSize: '2rem', fontWeight: 'bold' }}>
                  {guest.name.charAt(0).toUpperCase()}
                </Avatar>
              </Grid>
              <Grid size="grow">
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: theme.colors.text.primary,
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {guest.name}
                </Typography>
                {guest.lastName && (
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 400,
                      marginTop: '0.2rem',
                      color: theme.colors.text.secondary,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {guest.lastName}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.colors.text.secondary,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    mt: 0.5
                  }}
                >
                  Grupo: {guest.location || 'Sem grupo'}
                </Typography>
              </Grid>
            </Grid>

            {guest.notes ? (
              <>
                <Box sx={{ my: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: componentTokens.guestDetails.sectionTitle.color, mb: 1 }}>
                    Notas:
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.colors.text.secondary }}>
                    {guest.notes}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
              </>
            ) : (
              <Divider sx={{ my: 3 }} />
            )}

            {/* Alergias / Bebidas */}
            {(guest.allergies || (guest.drinks && guest.drinks.length > 0)) && (
              <>
                <Grid container spacing={4} sx={{ mb: 3 }}>
                  {guest.allergies && (
                    <Grid
                      size={{
                        xs: 12,
                        md: 6
                      }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: componentTokens.guestDetails.sectionTitle.color, mb: 1 }}>
                        Alergias/Intolerâncias:
                      </Typography>
                      <Typography variant="body1" sx={{ color: theme.colors.text.secondary }}>
                        {guest.allergies}
                      </Typography>
                    </Grid>
                  )}
                  {guest.drinks && guest.drinks.length > 0 && (
                    <Grid
                      size={{
                        xs: 12,
                        md: 6
                      }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: componentTokens.guestDetails.sectionTitle.color, mb: 1 }}>
                        Bebe:
                      </Typography>
                      <List dense sx={{ p: 0 }}>
                        {guest.drinks.map((drink, index) => (
                          <ListItem key={index} sx={{ p: 0 }}>
                            <ListItemIcon sx={{ minWidth: 'auto', mr: componentTokens.guestDetails.listItemIcon.marginRight, color: componentTokens.guestDetails.listItemIcon.color }}>
                              <FiberManualRecord sx={{ fontSize: '0.5rem' }} />
                            </ListItemIcon>
                            <ListItemText primary={drink} slotProps={{
                              primary: { color: theme.colors.text.secondary }
                            }} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                </Grid>
                <Divider sx={{ mb: 3 }} />
              </>
            )}

            {/* Preferências / Botões */}
            <Grid container spacing={4} alignItems="center" sx={{ mb: 3 }}>
              {guest.preferences && guest.preferences.length > 0 && (
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: componentTokens.guestDetails.sectionTitle.color, mb: 1 }}>
                    Preferências:
                  </Typography>
                  <List dense sx={{ p: 0 }}>
                    {guest.preferences.map((preference, index) => (
                      <ListItem key={index} sx={{ p: 0 }}>
                        <ListItemIcon sx={{ minWidth: 'auto', mr: componentTokens.guestDetails.listItemIcon.marginRight, color: componentTokens.guestDetails.listItemIcon.color }}>
                          <FiberManualRecord sx={{ fontSize: '0.5rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={preference} slotProps={{
                          primary: { color: theme.colors.text.secondary }
                        }} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}
              <Grid
                size={{
                  xs: 12,
                  md: guest.preferences && guest.preferences.length > 0 ? 6 : 12
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<AccessTime />}
                    sx={{
                      backgroundColor: componentTokens.guestDetails.button.background,
                      color: componentTokens.guestDetails.button.textColor,
                      borderRadius: componentTokens.guestDetails.button.borderRadius,
                      textTransform: 'none',
                      padding: componentTokens.guestDetails.button.padding,
                      fontWeight: 'bold',
                      '&:hover': { backgroundColor: componentTokens.guestDetails.button.hover },
                    }}
                  >
                    Histórico de visitas
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CalendarToday />}
                    onClick={handleOpenAddToEvent}
                    sx={{
                      backgroundColor: componentTokens.guestDetails.button.background,
                      color: componentTokens.guestDetails.button.textColor,
                      borderRadius: componentTokens.guestDetails.button.borderRadius,
                      textTransform: 'none',
                      padding: componentTokens.guestDetails.button.padding,
                      fontWeight: 'bold',
                      '&:hover': { backgroundColor: componentTokens.guestDetails.button.hover },
                    }}
                  >
                    Incluir na agenda de eventos
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* Informações Pessoais */}
            {(guest.maritalStatus || guest.hasChildren) && (
              <>
                <Box sx={{ backgroundColor: componentTokens.guestDetails.contactCard.background, borderRadius: 2, p: 3, mb: 3, color: componentTokens.guestDetails.contactCard.textColor }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Informações Pessoais:
                  </Typography>
                  {guest.maritalStatus && (
                    <Typography variant="body1">
                      Estado Civil: {getMaritalStatusLabel(guest.maritalStatus)}
                    </Typography>
                  )}
                  {guest.hasChildren !== undefined && (
                    <Typography variant="body1">
                      Filhos: {guest.hasChildren ? `Sim${guest.childrenCount ? ` (${guest.childrenCount})` : ''}` : 'Não'}
                    </Typography>
                  )}
                </Box>
              </>
            )}

            {/* Dados de Contato */}
            <Box sx={{ backgroundColor: componentTokens.guestDetails.contactCard.background, borderRadius: 2, p: 3, mb: 3, color: componentTokens.guestDetails.contactCard.textColor }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Dados:
              </Typography>
              {guest.phone && <Typography variant="body1">Tel: {guest.phone}</Typography>}
              {guest.email && <Typography variant="body1">Email: {guest.email}</Typography>}
              {guest.address && <Typography variant="body1">Endereço: {guest.address}</Typography>}
              {guest.city && <Typography variant="body1">Cidade: {guest.city}</Typography>}
              {guest.birthday && (
                <Typography variant="body1">
                  Data de aniversário: {new Date(guest.birthday).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </Typography>
              )}
            </Box>

            {/* Botão Enviar Convite */}
            <Button
              variant="contained"
              startIcon={<MailOutline />}
              onClick={handleSendInviteClick}
              fullWidth
              sx={{
                backgroundColor: componentTokens.guestDetails.button.background,
                color: componentTokens.guestDetails.button.textColor,
                borderRadius: componentTokens.guestDetails.button.borderRadius,
                textTransform: 'none',
                padding: componentTokens.guestDetails.button.padding,
                fontWeight: 'bold',
                fontSize: '1rem',
                '&:hover': { backgroundColor: componentTokens.guestDetails.button.hover },
              }}
            >
              Enviar convite para evento
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Dialog de adicionar a eventos */}
      <Dialog
        open={addToEventDialog}
        onClose={() => !addingToEvents && setAddToEventDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          Adicionar a Eventos
        </DialogTitle>
        <DialogContent>
          {loadingEvents ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress sx={{ color: theme.colors.primary }} />
            </Box>
          ) : userEvents.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography sx={{ color: theme.colors.text.secondary }}>
                Você não possui eventos futuros.
              </Typography>
              <Button
                onClick={() => {
                  setAddToEventDialog(false);
                  navigate('/events');
                }}
                sx={{
                  mt: 2,
                  color: theme.colors.primary,
                }}
              >
                Criar Evento
              </Button>
            </Box>
          ) : (
            <>
              <Typography sx={{ color: theme.colors.text.secondary, mb: 2 }}>
                Selecione os eventos onde deseja convidar {guest.name}:
              </Typography>
              <List>
                {userEvents.map((event) => {
                  const eventDate = new Date(event.date);
                  const formattedDate = eventDate.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  });

                  return (
                    <ListItemButton
                      key={event.id}
                      onClick={() => handleToggleEvent(event.id)}
                      sx={{
                        borderRadius: theme.radii.small,
                        mb: 1,
                        border: `1px solid ${theme.colors.border}`,
                      }}
                    >
                      <Checkbox
                        checked={selectedEvents.includes(event.id)}
                        sx={{
                          color: theme.colors.primary,
                          '&.Mui-checked': {
                            color: theme.colors.primary,
                          },
                        }}
                      />
                      <ListItemText
                        primary={event.title}
                        secondary={`${formattedDate} às ${event.time}`}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: theme.colors.text.primary,
                            fontWeight: 600,
                          },
                          '& .MuiListItemText-secondary': {
                            color: theme.colors.text.secondary,
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddToEventDialog(false)}
            disabled={addingToEvents}
            sx={{ color: theme.colors.text.secondary }}
          >
            Cancelar
          </Button>
          {userEvents.length > 0 && (
            <Button
              onClick={handleAddToEvents}
              disabled={addingToEvents}
              variant="contained"
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              {addingToEvents ? (
                <CircularProgress size={20} sx={{ color: 'inherit' }} />
              ) : (
                `Confirmar (${selectedEvents.length})`
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog: Select Event for Invite */}
      <Dialog
        open={showInviteEventDialog}
        onClose={() => setShowInviteEventDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          Selecionar Evento
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: theme.colors.text.secondary, mb: 2 }}>
            Selecione o evento para o qual deseja enviar o convite:
          </Typography>
          <List>
            {eventsForInvite.map((event) => {
              const eventDate = new Date(event.date);
              const formattedDate = eventDate.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              });
              return (
                <ListItemButton
                  key={event.id}
                  onClick={() => handleSelectEventForInvite(event)}
                  sx={{
                    borderRadius: theme.radii.small,
                    mb: 1,
                    border: `1px solid ${theme.colors.border}`,
                  }}
                >
                  <ListItemText
                    primary={event.title}
                    secondary={`${formattedDate} às ${event.time}`}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: theme.colors.text.primary,
                        fontWeight: 600,
                      },
                      '& .MuiListItemText-secondary': {
                        color: theme.colors.text.secondary,
                      },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInviteEventDialog(false)} sx={{ color: theme.colors.text.secondary }}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Select Invite Method */}
      <Dialog
        open={showInviteMethodDialog}
        onClose={() => setShowInviteMethodDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          Enviar Convite
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: theme.colors.text.secondary, mb: 3 }}>
            Como deseja enviar o convite para <b>{selectedEventForInvite?.title}</b>?
          </Typography>

          {guest.phone && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<WhatsApp />}
              onClick={handleSendWhatsApp}
              sx={{
                mb: 2,
                borderColor: '#25D366',
                color: '#25D366',
                '&:hover': {
                  borderColor: '#25D366',
                  backgroundColor: 'rgba(37, 211, 102, 0.04)'
                }
              }}
            >
              WhatsApp ({guest.phone})
            </Button>
          )}

          {guest.email && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<MailOutline />}
              onClick={handleSendEmail}
              sx={{
                mb: 2,
                color: theme.colors.text.primary,
                borderColor: theme.colors.border
              }}
            >
              Email ({guest.email})
            </Button>
          )}

          {!guest.phone && !guest.email && (
            <Alert severity="warning">
              Este convidado não possui telefone nem email cadastrados.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInviteMethodDialog(false)} sx={{ color: theme.colors.text.secondary }}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
